import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { Play, Square, Video, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'react-toastify';
import { startLiveHlsSession } from '../api/ropApi';

const GENERIC_ERROR_MESSAGE = 'No se pudo conectar con la cámara.';

const getErrorMessage = (error) => {
  const message = error?.response?.data?.message;
  // Only ever return a string: a non-string message (object/array from a
  // malformed or validation-style payload) would otherwise reach a React
  // child and crash the render — mirrors RopConfiguracionTab's guard.
  return typeof message === 'string' && message ? message : GENERIC_ERROR_MESSAGE;
};

// MediaMTX registers the HLS path on-demand when the session starts, so the
// manifest can literally not exist yet for the first couple of seconds. Give
// the initial load a short bounded retry instead of failing immediately —
// never an infinite retry loop.
const MANIFEST_RETRY_ATTEMPTS = 5;
const MANIFEST_RETRY_DELAY_MS = 2000;

// The backend contract says expiresInSeconds is always 60 today, but that
// shouldn't be trusted blindly — fall back to it only when the response is
// missing/malformed.
const SESSION_TTL_FALLBACK_SECONDS = 60;

const resolveTtlSeconds = (expiresInSeconds) =>
  typeof expiresInSeconds === 'number' && Number.isFinite(expiresInSeconds)
    ? expiresInSeconds
    : SESSION_TTL_FALLBACK_SECONDS;

const RopLiveViewTab = ({ uid }) => {
  const [status, setStatus] = useState('idle'); // idle | connecting | playing
  const [muted, setMuted] = useState(true);
  // Seconds left in the current "Tiempo del vivo" countdown; null while it
  // shouldn't be shown (not playing yet, or between attaching a source and
  // onPlaying starting it).
  const [secondsRemaining, setSecondsRemaining] = useState(null);

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const mediaErrorRecoveredRef = useRef(false);
  const mountedRef = useRef(true);
  // Total seconds the current session's countdown counts down from (the
  // server-reported expiresInSeconds, normalized).
  const totalSecondsRef = useRef(SESSION_TTL_FALLBACK_SECONDS);
  const countdownIntervalRef = useRef(null);
  // Bumped on every stopLive() and every attachSource() call — i.e. every
  // time the "current session" changes. Every async callback scheduled by a
  // session (retry timeout, the startLiveHlsSession promise) captures this
  // value at schedule time and re-checks it before doing anything, so a
  // stale callback from a superseded/stopped session always naturally
  // no-ops instead of needing separate ad hoc guards.
  const sessionGenerationRef = useRef(0);

  const clearTimers = () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const destroyPlayer = () => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    const video = videoRef.current;
    if (video) {
      video.onloadedmetadata = null;
      video.onerror = null;
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
  };

  const stopLive = () => {
    sessionGenerationRef.current += 1;
    clearTimers();
    destroyPlayer();
    retryCountRef.current = 0;
    if (mountedRef.current) {
      setStatus('idle');
      setSecondsRemaining(null);
    }
  };

  // Cleanup on unmount: never leak a running player or a pending timer when
  // the user switches tabs.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      sessionGenerationRef.current += 1;
      clearTimers();
      destroyPlayer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFatalFailure = () => {
    toast.error(GENERIC_ERROR_MESSAGE);
    stopLive();
  };

  // Hard, server-enforced cutoff mirrored client-side: counts down from the
  // session's expiresInSeconds and disconnects at zero. There is no
  // automatic renewal — the user must explicitly start a new session.
  const startCountdown = (generation) => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    let remaining = totalSecondsRef.current;
    setSecondsRemaining(remaining);
    countdownIntervalRef.current = setInterval(() => {
      if (sessionGenerationRef.current !== generation) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
        return;
      }
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
        setSecondsRemaining(0);
        stopLive();
        return;
      }
      setSecondsRemaining(remaining);
    }, 1000);
  };

  const attachSource = (hlsUrl, { expiresInSeconds } = {}) => {
    const video = videoRef.current;
    if (!video) return;

    // A new attach must cancel any pending retry timeout scheduled by the
    // instance it is about to replace — otherwise a stale timer can fire
    // later against a torn-down player.
    clearTimers();

    sessionGenerationRef.current += 1;
    retryCountRef.current = 0;
    mediaErrorRecoveredRef.current = false;
    totalSecondsRef.current = resolveTtlSeconds(expiresInSeconds);
    // Hidden until onPlaying (re)starts the countdown for this attach.
    setSecondsRemaining(null);

    const onPlaying = () => {
      if (!mountedRef.current) return;
      const generation = sessionGenerationRef.current;
      retryCountRef.current = 0;
      setStatus('playing');
      startCountdown(generation);
      video.play().catch(() => {});
    };

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari/iOS: native HLS, no hls.js instance involved.
      const tryLoad = () => {
        video.src = hlsUrl;
        video.load();
      };
      video.onloadedmetadata = onPlaying;
      video.onerror = () => {
        if (!mountedRef.current) return;
        const generation = sessionGenerationRef.current;
        if (retryCountRef.current < MANIFEST_RETRY_ATTEMPTS) {
          retryCountRef.current += 1;
          retryTimeoutRef.current = setTimeout(() => {
            retryTimeoutRef.current = null;
            if (sessionGenerationRef.current !== generation) return;
            tryLoad();
          }, MANIFEST_RETRY_DELAY_MS);
        } else {
          handleFatalFailure();
        }
      };
      tryLoad();
    } else if (Hls.isSupported()) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      const hls = new Hls();
      hlsRef.current = hls;
      hls.on(Hls.Events.MANIFEST_PARSED, onPlaying);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data?.fatal || !mountedRef.current) return;
        const generation = sessionGenerationRef.current;

        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          // "Manifest not ready yet" cold-start case: bounded retry, then
          // give up. Per hls.js's recovery contract this resumes loading
          // via startLoad(), not loadSource() (which would reset state as
          // if loading a brand-new source).
          if (retryCountRef.current < MANIFEST_RETRY_ATTEMPTS) {
            retryCountRef.current += 1;
            retryTimeoutRef.current = setTimeout(() => {
              retryTimeoutRef.current = null;
              if (sessionGenerationRef.current !== generation) return;
              hls.startLoad();
            }, MANIFEST_RETRY_DELAY_MS);
          } else {
            handleFatalFailure();
          }
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          // Not the cold-start problem the retry loop above was built for
          // — try hls.js's own recovery once, without touching the
          // manifest-retry budget. If it recurs right after recovery,
          // give up instead of looping media-error recovery forever.
          if (!mediaErrorRecoveredRef.current) {
            mediaErrorRecoveredRef.current = true;
            hls.recoverMediaError();
          } else {
            handleFatalFailure();
          }
        } else {
          handleFatalFailure();
        }
      });
      hls.loadSource(hlsUrl);
      hls.attachMedia(video);
    } else {
      toast.error('Tu navegador no soporta reproducción de video en vivo.');
      setStatus('idle');
    }
  };

  // The single entry point that starts a brand-new live session — invoked
  // only by the user's explicit "Ver en vivo" click. No code path calls this
  // again on its own: there is no automatic renewal once a session starts.
  const startLive = async () => {
    const expectedGeneration = sessionGenerationRef.current;
    setStatus('connecting');
    try {
      const { hlsUrl, expiresInSeconds } = await startLiveHlsSession(uid, {
        channel: 0,
        streamType: 'mainStream',
      });
      if (!mountedRef.current || sessionGenerationRef.current !== expectedGeneration) return;
      attachSource(hlsUrl, { expiresInSeconds });
    } catch (error) {
      if (!mountedRef.current || sessionGenerationRef.current !== expectedGeneration) return;
      toast.error(getErrorMessage(error));
      setStatus('idle');
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
  };

  if (!uid) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <p className="text-sm text-gray-600 text-center">
          Esta cámara no tiene un dispositivo ROP vinculado.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
            <Video size={18} />
          </div>
          <h3 className="text-base font-semibold text-gray-800">Ver en vivo</h3>
        </div>

        <div className="relative w-full aspect-video rounded-lg bg-black overflow-hidden flex items-center justify-center">
          <video
            ref={videoRef}
            muted={muted}
            playsInline
            className={`w-full h-full object-contain ${status === 'playing' ? '' : 'hidden'}`}
          />

          {status === 'idle' && (
            <button
              type="button"
              onClick={() => startLive()}
              className="flex flex-col items-center gap-2 text-white hover:text-blue-300 transition-colors"
            >
              <Play size={40} />
              <span className="text-sm">Ver en vivo</span>
            </button>
          )}

          {status === 'connecting' && (
            <p className="text-sm text-white text-center px-4">Conectando con la cámara…</p>
          )}

          {status === 'playing' && (
            <button
              type="button"
              aria-label={muted ? 'Activar sonido' : 'Silenciar'}
              onClick={toggleMute}
              className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
            >
              {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
          )}

          {status === 'playing' && secondsRemaining !== null && (
            <div className="absolute top-0 left-0 right-0 px-3 pt-2 pb-3 bg-gradient-to-b from-black/70 to-transparent">
              <div className="flex items-center justify-between text-[11px] text-white/90 mb-1">
                <span>Tiempo del vivo</span>
                <span>{secondsRemaining}s</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-white/20 overflow-hidden">
                <div
                  role="progressbar"
                  aria-label="Tiempo del vivo"
                  aria-valuemin={0}
                  aria-valuemax={totalSecondsRef.current}
                  aria-valuenow={secondsRemaining}
                  className="h-full bg-blue-400"
                  style={{
                    width: `${
                      totalSecondsRef.current > 0
                        ? Math.max((secondsRemaining / totalSecondsRef.current) * 100, 0)
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {status === 'playing' && (
          <button
            type="button"
            onClick={stopLive}
            className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm transition-colors"
          >
            <Square size={16} />
            Detener
          </button>
        )}
      </div>
    </div>
  );
};

export default RopLiveViewTab;
